import { useEffect, useRef, useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/index.js';
import type { SSEEvent, SessionStatus } from '../api/types.js';
import { lastAgentEventId } from './event-cursor.js';

const API_URL = import.meta.env.VITE_API_URL || '';

export type OutputStreamStatus = 'idle' | 'connecting' | 'open' | 'closed' | 'error';

export function useSessionOutput(
  serverId: string,
  sessionId: string,
  status: SessionStatus | undefined,
) {
  const { getIdToken } = useAuth();
  const queryClient = useQueryClient();
  const abortRef = useRef<AbortController | null>(null);
  const [streamStatus, setStreamStatus] = useState<OutputStreamStatus>('idle');

  const isActive = status === 'processing' || status === 'waiting_for_input' || status === 'active';

  const appendEvents = useCallback((newEvents: SSEEvent[]) => {
    if (newEvents.length === 0) return;
    queryClient.setQueryData<SSEEvent[]>(
      ['session-history', serverId, sessionId],
      (existing) => {
        const prev = existing ?? [];
        // Deduplicate by id
        const existingIds = new Set(prev.map((e) => e.id));
        const unique = newEvents.filter((e) => !existingIds.has(e.id));
        return unique.length > 0 ? [...prev, ...unique] : prev;
      },
    );
  }, [queryClient, serverId, sessionId]);

  const connect = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();

    const controller = new AbortController();
    abortRef.current = controller;
    setStreamStatus('connecting');

    let token: string;
    try {
      token = await getIdToken();
    } catch {
      if (!controller.signal.aborted) setStreamStatus('error');
      return;
    }

    // Resume from the last agent event seen (persisted prompts carry epoch ids, see event-cursor.ts)
    const existing = queryClient.getQueryData<SSEEvent[]>(['session-history', serverId, sessionId]);
    const lastAgentId = lastAgentEventId(existing);
    const lastId = lastAgentId > 0 ? String(lastAgentId) : undefined;

    try {
      const headers: Record<string, string> = {
        Authorization: `Bearer ${token}`,
        Accept: 'text/event-stream',
      };
      if (lastId) {
        headers['Last-Event-ID'] = lastId;
      }

      const response = await fetch(
        `${API_URL}/servers/${serverId}/sessions/${sessionId}/output${lastId ? `?lastEventId=${lastId}` : ''}`,
        { headers, signal: controller.signal },
      );

      if (!response.ok || !response.body) {
        throw new Error(`SSE connection failed: ${response.status}`);
      }

      setStreamStatus('open');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let currentEvent = '';
      let currentData = '';
      let currentId = '';

      function flushEvent() {
        if (!currentData) return;
        try {
          const parsed = JSON.parse(currentData) as Record<string, unknown>;
          const sseEvent: SSEEvent = {
            id: currentId ? parseInt(currentId, 10) : Date.now(),
            event: (currentEvent || 'output') as SSEEvent['event'],
            data: parsed,
            timestamp: new Date().toISOString(),
          };
          appendEvents([sseEvent]);
          // The turn ended (or needs input): refresh the session record now rather than
          // waiting for the status poll, which the browser pauses while the tab is hidden.
          if (sseEvent.event === 'done' || sseEvent.event === 'waiting' || sseEvent.event === 'error') {
            queryClient.invalidateQueries({ queryKey: ['session', serverId, sessionId] });
          }
        } catch {
          // Skip malformed events
        }
        currentEvent = '';
        currentData = '';
        currentId = '';
      }

      function processLines(lines: string[]) {
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            currentEvent = line.slice(7);
          } else if (line.startsWith('data: ')) {
            currentData = line.slice(6);
          } else if (line.startsWith('id: ')) {
            currentId = line.slice(4);
          } else if (line === '') {
            flushEvent();
          }
        }
      }

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';
        processLines(lines);
      }

      // Flush remaining buffer
      if (buffer) {
        processLines(buffer.split('\n'));
      }
      flushEvent();
    } catch {
      if (controller.signal.aborted) return;
      setStreamStatus('error');
    }

    if (!controller.signal.aborted) {
      setStreamStatus('closed');
    }
  }, [getIdToken, serverId, sessionId, queryClient, appendEvents]);

  useEffect(() => {
    if (!isActive) {
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
      setStreamStatus('idle');
      return;
    }

    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    async function connectWithRetry() {
      while (!cancelled) {
        await connect();
        if (cancelled) break;
        // Reconnect after a short delay when stream closes while session is still active
        await new Promise<void>((resolve) => {
          reconnectTimer = setTimeout(resolve, 2_000);
        });
      }
    }

    connectWithRetry();

    return () => {
      cancelled = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
    };
  }, [isActive, connect]);

  return { streamStatus, isStreaming: streamStatus === 'open' };
}